import DEFAULTS from "./importCodes.defaults.json";

/**
 * Sermon-plan import codes of a Gemeinde (GOD-48), as edited on the import page.
 * Mirrors functions/src/importConfig.ts; the defaults are shared through
 * importCodes.defaults.json (a functions test compares both).
 */
export type ImportCodeKind = "houses" | "serviceCodes";

export const IMPORT_CODE_FIELDS: Record<ImportCodeKind, string> = {
    houses: "importHouses",
    serviceCodes: "importServiceCodes",
};

export const DEFAULT_IMPORT_CODES: Record<ImportCodeKind, Record<string, string>> = DEFAULTS;

export const MAX_IMPORT_CODES = 100;
export const MAX_IMPORT_CODE_LENGTH = 12;
export const MAX_IMPORT_NAME_LENGTH = 80;

export interface ImportCodeRow {
    code: string;
    name: string;
}

/** Same rule as the functions: no blanks, nothing that reads like a slot ("1", "3-SN"). */
export function isValidImportCode(code: string): boolean {
    return code.length > 0 && code.length <= MAX_IMPORT_CODE_LENGTH && !/\s/.test(code) && !/^[1-4](-|$)/.test(code);
}

/** Rows to edit: the stored map, or the defaults when the Gemeinde never saved one. */
export function importCodeRowsOf(settings: Record<string, unknown>, kind: ImportCodeKind): ImportCodeRow[] {
    const stored = settings[IMPORT_CODE_FIELDS[kind]];
    const map =
        stored && typeof stored === "object" && !Array.isArray(stored)
            ? (stored as Record<string, unknown>)
            : DEFAULT_IMPORT_CODES[kind];
    return Object.entries(map)
        .filter((entry): entry is [string, string] => typeof entry[1] === "string")
        .map(([code, name]) => ({ code, name }));
}

export type ImportCodeError = "invalidCode" | "missingName" | "nameTooLong" | "duplicate" | "tooMany";

/**
 * The map to store, or the problems (i18n keys under admin.importCodes.errors)
 * with the code they concern. Blank rows are ignored.
 */
export function importCodeMapOf(rows: ImportCodeRow[]): {
    map: Record<string, string>;
    errors: { error: ImportCodeError; code: string }[];
} {
    const map: Record<string, string> = {};
    const errors: { error: ImportCodeError; code: string }[] = [];
    const filled = rows
        .map((row) => ({ code: row.code.trim(), name: row.name.trim() }))
        .filter((row) => row.code || row.name);
    if (filled.length > MAX_IMPORT_CODES) errors.push({ error: "tooMany", code: "" });
    for (const { code, name } of filled) {
        if (!isValidImportCode(code)) errors.push({ error: "invalidCode", code });
        else if (!name) errors.push({ error: "missingName", code });
        else if (name.length > MAX_IMPORT_NAME_LENGTH) errors.push({ error: "nameTooLong", code });
        // Codes are matched exactly, so "AM" and "am" are two codes; the same spelling twice is an error.
        else if (Object.prototype.hasOwnProperty.call(map, code)) errors.push({ error: "duplicate", code });
        else map[code] = name;
    }
    return { map, errors };
}
